/* eslint-disable camelcase */
import {
  MerchantCodeByRate,
  AutoliquidatedType,
} from "../store/ducks/merchantCodesByRate.ducks"
import { FullDocumentOnly } from "../store/ducks/inputMask.ducks"
import { WritingLinePayload, AllEntryDatumTypes } from "./inputMask"

export interface ResolvedMerchantCodeByRate {
  merchantCodeByRate: MerchantCodeByRate
  matchingLines: WritingLinePayload[]
}

const DATUM_TYPE_TO_AUTOLIQUIDATED_TYPE: Record<string, AutoliquidatedType> = {
  construction_vat: "construction",
  construction_reverse_vat: "construction",
  eu_vat: "eu",
  eu_reverse_vat: "eu",
  eu_goods_vat: "eu_goods",
  eu_goods_reverse_vat: "eu_goods",
  world_vat: "world",
  world_reverse_vat: "world",
  world_goods_vat: "world_goods",
  world_goods_reverse_vat: "world_goods",
}

export const compareMerchantCodesByRateArrays = (
  original: MerchantCodeByRate[],
  edited: MerchantCodeByRate[]
): boolean => {
  const cleanOriginal = original.filter(
    (item, index) =>
      !(
        index === original.length - 1 &&
        item.vat_rate_id === null &&
        item.autoliquidated_type === null &&
        item.id === null &&
        item.merchant_code_id === null &&
        item.merchant_id === null
      )
  )
  const cleanEdited = edited.filter(
    (item, index) =>
      !(
        index === edited.length - 1 &&
        item.vat_rate_id === null &&
        item.autoliquidated_type === null &&
        item.id === null &&
        item.merchant_code_id === null &&
        item.merchant_id === null
      )
  )

  if (cleanOriginal.length !== cleanEdited.length) {
    return false
  }

  return cleanOriginal.every((originalItem, index) => {
    const editedItem = cleanEdited[index]
    return (
      originalItem.vat_rate_id === editedItem.vat_rate_id &&
      originalItem.autoliquidated_type === editedItem.autoliquidated_type &&
      originalItem.id === editedItem.id &&
      originalItem.merchant_code_id === editedItem.merchant_code_id &&
      originalItem.merchant_id === editedItem.merchant_id
    )
  })
}

export const canSaveMerchantCodesByRate = (
  edited: MerchantCodeByRate[]
): boolean => {
  if (edited.length === 0) {
    return true
  }

  const nonEmptyLines = edited.filter((item, index) => {
    const isLastLine = index === edited.length - 1
    const isEmpty =
      item.vat_rate_id === null &&
      item.autoliquidated_type === null &&
      item.merchant_code_id === null &&
      item.id === null &&
      item.merchant_id === null

    return !(isLastLine && isEmpty)
  })

  return nonEmptyLines.every((item) => {
    const hasVatRate = item.vat_rate_id !== null
    const hasAutoliquidatedType = item.autoliquidated_type !== null
    const hasMerchantCodeId = item.merchant_code_id !== null

    return (hasVatRate || hasAutoliquidatedType) && hasMerchantCodeId
  })
}

export const resolveMerchantCodeByRate = (
  merchantCodesByRates: MerchantCodeByRate[],
  editedFullDocument: FullDocumentOnly,
  editedWritingLines: WritingLinePayload[]
): ResolvedMerchantCodeByRate | null => {
  if (!editedFullDocument.merchant_id) {
    return null
  }

  const merchantCodesByRatesForMerchant = merchantCodesByRates.filter(
    (mcbr) => mcbr.merchant_id === editedFullDocument.merchant_id
  )

  if (merchantCodesByRatesForMerchant.length === 0) {
    return null
  }

  const datumTypes = editedWritingLines
    .map((line) => line.datum_type)
    .filter((datumType) => datumType !== "tax_included")
    .filter((datumType, index, array) => array.indexOf(datumType) === index)

  if (datumTypes.length === 0) {
    return null
  }

  const matchingMerchantCodeByRate = findMatchingMerchantCodeByRate(
    merchantCodesByRatesForMerchant,
    datumTypes,
    editedWritingLines
  )

  if (!matchingMerchantCodeByRate) {
    return null
  }

  const matchingLines = editedWritingLines.filter((line) => {
    if (matchingMerchantCodeByRate.autoliquidated_type) {
      const mappedType = DATUM_TYPE_TO_AUTOLIQUIDATED_TYPE[line.datum_type]
      return mappedType === matchingMerchantCodeByRate.autoliquidated_type
    } else if (
      matchingMerchantCodeByRate.vat_rate_id &&
      matchingMerchantCodeByRate.vat_rate_value
    ) {
      if (DATUM_TYPE_TO_AUTOLIQUIDATED_TYPE[line.datum_type]) {
        return false
      }

      return (
        Number(line.vat_rate) ===
        Number(matchingMerchantCodeByRate.vat_rate_value)
      )
    }
    return false
  })

  return {
    merchantCodeByRate: matchingMerchantCodeByRate,
    matchingLines,
  }
}

const findMatchingMerchantCodeByRate = (
  merchantCodesByRates: MerchantCodeByRate[],
  datumTypes: AllEntryDatumTypes[],
  writingLines: WritingLinePayload[]
): MerchantCodeByRate | null => {
  const matches = merchantCodesByRates.filter((mcbr) =>
    matchesDatumTypes(mcbr, datumTypes, writingLines)
  )

  return matches.length === 1 ? matches[0] : null
}

const matchesDatumTypes = (
  merchantCodeByRate: MerchantCodeByRate,
  datumTypes: AllEntryDatumTypes[],
  writingLines: WritingLinePayload[]
): boolean => {
  if (merchantCodeByRate.autoliquidated_type) {
    const expectedAutoliquidatedType = merchantCodeByRate.autoliquidated_type
    return datumTypes.every((datumType) => {
      const mappedType = DATUM_TYPE_TO_AUTOLIQUIDATED_TYPE[datumType]
      return mappedType === expectedAutoliquidatedType
    })
  } else if (
    merchantCodeByRate.vat_rate_id &&
    merchantCodeByRate.vat_rate_value
  ) {
    const expectedVatRate = merchantCodeByRate.vat_rate_value

    const forbiddenDatumTypes = ["custom", "deposit", "reverse_deposit"]
    if (
      forbiddenDatumTypes.some((datumType) =>
        datumTypes.includes(datumType as AllEntryDatumTypes)
      )
    ) {
      return false
    }

    const allVatRatesAreSame = writingLines
      .filter(
        (line) =>
          !["custom", "deposit", "reverse_deposit", "tax_included"].includes(
            line.datum_type
          )
      )
      .every((line) => Number(line.vat_rate) === Number(expectedVatRate))

    if (!allVatRatesAreSame) return false

    return datumTypes.every((datumType) => {
      if (DATUM_TYPE_TO_AUTOLIQUIDATED_TYPE[datumType]) {
        return false
      }

      const linesWithThisDatumType = writingLines.filter(
        (line) => line.datum_type === datumType
      )

      if (linesWithThisDatumType.length === 0) return false

      return linesWithThisDatumType.every(
        (line) => Number(line.vat_rate) === Number(expectedVatRate)
      )
    })
  }

  return false
}
